import { useEffect, useState } from "react";

/**
 * Indikator muat global.
 *
 * Dipasang dengan membungkus `window.fetch` sekali, bukan dengan menambah state
 * di setiap halaman: filter tanggal ada di sepuluh halaman, dan menambahkan
 * penanda satu per satu berarti sembilan kesempatan untuk lupa.
 *
 * Dua aturan yang membuatnya tidak mengganggu:
 *  1. Baru muncul setelah 150 ms. Permintaan cepat tidak boleh menghasilkan kedip.
 *  2. Polling latar TIDAK dihitung. Halaman Sync menarik data tiap 5 detik dan
 *     Topbar menarik kesegaran saat dimuat; bar yang berkedip tiap 5 detik justru
 *     persis gangguan yang diminta untuk dihindari.
 */

let aktif = 0;
let senyap = 0; // >0 berarti panggilan fetch saat ini adalah polling latar
const pendengar = new Set<(n: number) => void>();
const kabari = () => pendengar.forEach((f) => f(aktif));

/** Jalankan fetch tanpa memicu indikator (dipakai polling berkala). */
export function latar<T>(fn: () => T): T {
  senyap++;
  try { return fn(); } finally { senyap--; }
}

declare global { interface Window { __avioFetchDibungkus?: boolean } }

export function pasangPelacakFetch() {
  if (typeof window === "undefined" || window.__avioFetchDibungkus) return;
  window.__avioFetchDibungkus = true;
  const asli = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (senyap > 0) return asli(input, init);
    aktif++; kabari();
    // finally, bukan then: permintaan yang gagal juga harus menurunkan hitungan,
    // kalau tidak bar akan menyala selamanya setelah satu error jaringan.
    return asli(input, init).finally(() => { aktif = Math.max(0, aktif - 1); kabari(); });
  }) as typeof window.fetch;
}

export function ProgressBar() {
  const [tampil, setTampil] = useState(false);

  useEffect(() => {
    let tunda: ReturnType<typeof setTimeout> | null = null;
    let padam: ReturnType<typeof setTimeout> | null = null;
    const onUbah = (n: number) => {
      if (n > 0) {
        if (padam) { clearTimeout(padam); padam = null; }
        if (!tunda) tunda = setTimeout(() => { tunda = null; setTampil(true); }, 150);
      } else {
        if (tunda) { clearTimeout(tunda); tunda = null; }
        // Jeda pendek sebelum padam supaya rentetan permintaan berturut-turut
        // tidak terlihat sebagai bar yang berkedip-kedip.
        padam = setTimeout(() => setTampil(false), 220);
      }
    };
    pendengar.add(onUbah);
    return () => {
      pendengar.delete(onUbah);
      if (tunda) clearTimeout(tunda);
      if (padam) clearTimeout(padam);
    };
  }, []);

  return (
    <>
      <style>{`
        @keyframes avio-garis { from { background-position: 0 0; } to { background-position: 40px 0; } }
        .avio-progress {
          position: fixed; inset: 0 0 auto 0; height: 3px; z-index: 200;
          pointer-events: none; opacity: 0; transition: opacity .18s ease;
        }
        .avio-progress[data-on="1"] { opacity: 1; }
        .avio-progress__garis {
          height: 100%;
          background-image: repeating-linear-gradient(
            115deg,
            var(--brand-500, #0055FF) 0 12px,
            color-mix(in srgb, var(--brand-500, #0055FF) 45%, transparent) 12px 20px
          );
          background-size: 40px 100%;
          animation: avio-garis .7s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .avio-progress__garis { animation: none; }
        }
      `}</style>
      <div className="avio-progress" data-on={tampil ? "1" : "0"} role="status" aria-live="polite"
        aria-label={tampil ? "Memuat data" : undefined}>
        <div className="avio-progress__garis" />
      </div>
    </>
  );
}
